import React, { useState } from 'react';
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	TextField,
	Button,
	MenuItem,
	Stack,
	Alert,
	Chip,
	IconButton,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import {
	createCredentialRequest,
	skuParentChoices,
	type CreateCredentialRequestOutput,
} from '../../Services/credentialRequests';

interface CreateCredentialRequestModalProps {
	open: boolean;
	onClose: () => void;
	onCreated: (result: CreateCredentialRequestOutput) => void;
}

const CreateCredentialRequestModal: React.FC<CreateCredentialRequestModalProps> = ({
	open,
	onClose,
	onCreated,
}) => {
	const [sku, setSku] = useState('');
	const [skuParent, setSkuParent] = useState('advertiser');
	const [clientName, setClientName] = useState('');
	const [note, setNote] = useState('');
	const [expiresInDays, setExpiresInDays] = useState(7);
	const [emailInput, setEmailInput] = useState('');
	const [notifyEmails, setNotifyEmails] = useState<string[]>([]);
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);

	const handleAddEmail = () => {
		const email = emailInput.trim();
		if (email && email.includes('@') && !notifyEmails.includes(email)) {
			setNotifyEmails([...notifyEmails, email]);
			setEmailInput('');
		}
	};

	const handleRemoveEmail = (email: string) => {
		setNotifyEmails(notifyEmails.filter((e) => e !== email));
	};

	const handleEmailKeyDown = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			handleAddEmail();
		}
	};

	const handleSubmit = async () => {
		setError(null);

		if (!sku) {
			setError('SKU is required');
			return;
		}
		if (notifyEmails.length === 0) {
			setError('At least one notification email is required');
			return;
		}

		setLoading(true);
		try {
			const result = await createCredentialRequest({
				sku,
				skuParent,
				notifyEmails,
				clientName: clientName || undefined,
				note: note || undefined,
				expiresInDays,
			});
			onCreated(result);
			handleReset();
		} catch (err: any) {
			setError(err?.error || err?.message || 'Failed to create credential request');
		} finally {
			setLoading(false);
		}
	};

	const handleReset = () => {
		setSku('');
		setSkuParent('advertiser');
		setClientName('');
		setNote('');
		setExpiresInDays(7);
		setEmailInput('');
		setNotifyEmails([]);
		setError(null);
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
			<DialogTitle>New Credential Request</DialogTitle>
			<DialogContent>
				<Stack spacing={2} sx={{ mt: 1 }}>
					{error && <Alert severity="error">{error}</Alert>}

					<TextField
						label="Connector SKU"
						value={sku}
						onChange={(e) => setSku(e.target.value)}
						required
						placeholder="e.g. googleads, meta, prestashop-sql"
						size="small"
					/>

					<TextField
						label="SKU Parent"
						select
						value={skuParent}
						onChange={(e) => setSkuParent(e.target.value)}
						size="small"
					>
						{skuParentChoices.map((c) => (
							<MenuItem key={c.id} value={c.id}>
								{c.name}
							</MenuItem>
						))}
					</TextField>

					<TextField
						label="Client Name"
						value={clientName}
						onChange={(e) => setClientName(e.target.value)}
						placeholder="Optional — displayed on the public page"
						size="small"
					/>

					<TextField
						label="Note"
						value={note}
						onChange={(e) => setNote(e.target.value)}
						placeholder="Optional internal note"
						size="small"
						multiline
						rows={2}
					/>

					<TextField
						label="Expires in (days)"
						type="number"
						value={expiresInDays}
						onChange={(e) => setExpiresInDays(parseInt(e.target.value) || 7)}
						size="small"
						inputProps={{ min: 1, max: 90 }}
					/>

					<div>
						<Stack direction="row" spacing={1} alignItems="center">
							<TextField
								label="Notification Emails"
								value={emailInput}
								onChange={(e) => setEmailInput(e.target.value)}
								onKeyDown={handleEmailKeyDown}
								placeholder="email@example.com"
								size="small"
								fullWidth
							/>
							<IconButton onClick={handleAddEmail} color="primary" size="small">
								<AddIcon />
							</IconButton>
						</Stack>
						{notifyEmails.length > 0 && (
							<Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
								{notifyEmails.map((email) => (
									<Chip
										key={email}
										label={email}
										size="small"
										onDelete={() => handleRemoveEmail(email)}
									/>
								))}
							</Stack>
						)}
					</div>
				</Stack>
			</DialogContent>
			<DialogActions>
				<Button onClick={onClose} color="secondary">
					Cancel
				</Button>
				<Button onClick={handleSubmit} variant="contained" disabled={loading}>
					{loading ? 'Creating...' : 'Create'}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

export default CreateCredentialRequestModal;
